import React from 'react';
import {
    Button,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    FormGroup,
    Badge,
    UncontrolledTooltip,
} from 'reactstrap';
import cls from 'classnames';
import { v7 as uuid } from 'uuid';
import { groupBy, mapValues, remove } from 'lodash-es';
import * as moment from 'moment';
import {
    faBan,
    faUserClock,
    faExclamationCircle,
    faExclamationTriangle,
    faMinusCircle,
    faFileImage,
    faUpload,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18n from '@s20/i18n';
import { formatMinutes } from '@s20/common/util/number';
import { httpClient } from '@s20/services/httpClient';
import { BaseFormViewModel } from '../common/ViewModel';
import {
    FormCheckbox,
    FlexBetweenRow,
    FormLabel,
    onFieldChange,
    onReactSelectChanged,
    toasty,
    FlexStartRow,
    FormBlocker,
    Input,
    NumericInput,
    Textarea,
} from '../common/forms/FormElements';
import CommonContext, {
    ApiRoutes, ChargeTypeResourceTypes, RentalUnitTypes, TimesheetAttachmentScope,
} from '../Common';
import SlideForm from '../common/forms/SlideForm';
import EmailRecipients from '../common/forms/EmailRecipients';
import FileUpload from '../common/forms/FileUpload';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { ChargeTypeEligbility } from '../contracts/Contract';
import { Address } from '../address/Address';
import AddressFormNew from '../address/AddressFormNew';
import { ChargeTypeUnits } from '../chargeType/ChargeType';
import TimePicker from '../common/forms/TimePicker';
import { DatePicker } from '../common/forms/DatePicker';
import { Timesheet } from './Timesheet';
import './TimesheetItem.scss';

const WorkOrderType = {
    Required: 1,
    Optional: 2,
    NotApplicable: 3,
};

const ChargeTypeTrackingType = {
    Flagging: 1,
    DriveTimeTo: 2,
    DriveTimeFrom: 3,
};

const t = i18n.t.bind(i18n);

const PAINT_BATCH_CODE_MAX_LENGTH = 50;
const PAINT_BATCH_CODES_MAX_COUNT = 10;

// The comma-joined codes have to fit the Timesheet.PaintBatchCodes column. Ten codes at the
// per-code limit plus their separators would exceed it, so this is the backstop that does.
const PAINT_BATCH_CODES_MAX_LENGTH = 500;

const formatAddress = (location) => {
    if (!location) return '';
    return `${location.address1}${location.address2 ? ` ${location.address2}` : ''} ${location.city}, ${location.state} ${location.zip}`;
};

const onFileDownload = (file) => {
    if (file?.id) {
        httpClient.open(ApiRoutes.fileUpload.getAttachmentById(file.id), '_blank');
    }
};

const isHourlyTimeRow = (detail) => !!detail
    && detail.chargeTypeUnitsId !== ChargeTypeUnits.Flat
    && !(detail.chargeType?.payrollChargeTypeId > 0);

const isDriveTimeRow = (detail) => {
    const trackingTypeId = detail?.chargeType?.baseConfig?.chargeTypeTrackingTypeId;
    return trackingTypeId === ChargeTypeTrackingType.DriveTimeTo
        || trackingTypeId === ChargeTypeTrackingType.DriveTimeFrom;
};

const isCascadeRow = (detail) => isHourlyTimeRow(detail)
    && !detail.removed
    && !detail.notApplicable
    && !isDriveTimeRow(detail);

const isDependentEquipmentRow = (detail) => isCascadeRow(detail)
    && !!detail.jobAssignment
    && (detail.jobAssignment.equipmentEmployeeAssignmentId
        || detail.jobAssignment.equipmentAssignedEquipmentId);

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const dependsOnSource = (dependent, source) => {
    const ja = dependent.jobAssignment;

    return ja.equipmentAssignedEquipmentId
        ? sameId(source.jobAssignment?.assignedEquipmentId, ja.equipmentAssignedEquipmentId)
        : sameId(source.jobAssignment?.assignedEmployeeId, ja.equipmentEmployeeAssignmentId);
};

const cascadeHourlyEquipmentTimes = (timesheetDetails, changedRowIds) => {
    const updates = new Map(); // detail.id -> { start, end }
    const queue = [...changedRowIds];

    const timesOf = (detail) => updates.get(detail.id) ?? { start: detail.start, end: detail.end };

    while (queue.length > 0) {
        const sourceId = queue.shift();
        const source = timesheetDetails.find((d) => d.id === sourceId);

        if (!source || !isCascadeRow(source)) {
            continue;
        }

        const { start, end } = timesOf(source);

        timesheetDetails.forEach((detail) => {
            if (detail.id === source.id
                || !isDependentEquipmentRow(detail)
                || detail.isBreak !== source.isBreak
                || !dependsOnSource(detail, source)) {
                return;
            }

            const current = timesOf(detail);

            if (current.start === start && current.end === end) {
                return;
            }

            updates.set(detail.id, { start, end });
            queue.push(detail.id); // this equipment may itself be the parent of other equipment
        });
    }

    if (updates.size === 0) {
        return timesheetDetails;
    }

    return timesheetDetails.map((detail) => (updates.has(detail.id)
        ? { ...detail, ...updates.get(detail.id) }
        : detail));
};

export default class TimesheetItem extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.addressFormRef = React.createRef();
        this.meetingAddressFormRef = React.createRef();

        const stateBase = {
            jobLocationOverride: new Address(),
            meetingLocationOverride: new Address(),
            addChargeDropdownOpen: false,
            timesheet: {},
            chargeTypes: [],
            optionalChargeTypes: [],
            timesheetValid: false,
            currentPaintBatchCode: '',
            attachments: [],
            attachmentsLoading: false,
            visibleChargeTypeIds: [],
            saving: false,
            allowDefaultChargeTypeValues: false,
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onChange = this.onChange.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.resetForm = this.resetForm.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onAddChargeTypeToTimesheet = (chargeType) => {
        const {
            optionalChargeTypes, visibleChargeTypeIds, chargeTypes, timesheet,
        } = this.state;

        const alreadyExists = visibleChargeTypeIds.includes(chargeType.id);

        const updatedChargeTypeIndex = chargeTypes.indexOf(
            chargeTypes.find((c) => c.id === chargeType.id),
        );

        if (!chargeType.requiresWorkflowNotes) {
            remove(optionalChargeTypes, chargeType);
        }

        visibleChargeTypeIds.push(chargeType.id);
        chargeTypes[updatedChargeTypeIndex].include = true;

        if (chargeType.payrollChargeTypeId > 0) {
            const timesheetDetails = timesheet.timesheetDetails.map((detail) => {
                if (detail.chargeTypeId === chargeType.id) {
                    return {
                        ...detail,
                        start: '00:00',
                        end: '01:00',
                        value: 1,
                    };
                }

                return detail;
            });

            this.setState({ timesheet: { ...timesheet, timesheetDetails } });
        } else if (chargeType.requiresWorkflowNotes && alreadyExists) {
            const existingDetail = timesheet.timesheetDetails.find((detail) => detail.chargeTypeId === chargeType.id);

            this.setState({
                timesheet: {
                    ...timesheet,
                    timesheetDetails: timesheet.timesheetDetails.concat({
                        ...existingDetail,
                        id: uuid(),
                        value: 0,
                        workflowNotes: '',
                        notApplicable: false,
                    }),
                },
            });
        }

        this.setState({
            optionalChargeTypes,
            visibleChargeTypeIds,
            chargeTypes,
        });
    };

    onAddJobLocationAddress = () => {
        const { timesheet } = { ...this.state };
        const tenant = this.context?.tenant ?? null;
        const existing = timesheet.jobLocationAddress ?? timesheet.originalJobLocationAddress;
        const countryId = existing?.countryId ?? tenant?.countryId;
        const countryCode = existing?.countryCode ?? tenant?.countryCode;
        const addressToOpen = existing ? { ...existing } : new Address(countryId, countryCode);
        addressToOpen.id = null;
        this.addressFormRef.current.open(
            addressToOpen,
            'Job Location Override',
            false,
            false,
        );
    };

    // Handler for populating the same entered time values to all values of that charge type
    // ("Lazy user mode")
    onAllCheckChanged = (e) => {
        const { timesheet, chargeTypes } = this.state;

        chargeTypes.find((x) => x.name == e.target.name).sameAll = e.target.checked;

        if (e.target.checked === true) {
            const details = timesheet.timesheetDetails.filter(
                (x) => x.chargeTypeName == e.target.name && !x.removed,
            );

            const first = details.find((d) => !d.notApplicable);

            if (!first) {
                this.setState({ chargeTypes, timesheet });
                return;
            }

            details.forEach((x) => {
                if (x.notApplicable) {
                    return;
                }

                x.start = first.start;
                x.end = first.end;
                x.value = first.value;
            });

            timesheet.timesheetDetails = cascadeHourlyEquipmentTimes(
                timesheet.timesheetDetails,
                details.filter((x) => !x.notApplicable).map((x) => x.id),
            );
        }

        this.setState({ chargeTypes, timesheet });
    };

    onChange = onFieldChange;

    onChargeChange = (resources, ev) => {
        const { timesheet } = this.state;
        const val = ev.target.value;
        const { name } = ev.target;

        // We display the options in 12-hour format, but the db has 24.
        const timeValue = ev.target.value
            ? moment(val, ['h:mm A']).format('HH:mm')
            : '';

        let timesheetDetails = timesheet.timesheetDetails.map((detail) => {
            const resource = resources.findIndex((x) => x.id === detail.id);

            if (resource === -1) {
                return detail;
            }

            return {
                ...detail,
                [name]: detail.chargeTypeUnitsId == ChargeTypeUnits.Flat ? val : timeValue,
            };
        });

        if (name === 'start' || name === 'end') {
            timesheetDetails = cascadeHourlyEquipmentTimes(
                timesheetDetails,
                resources.map((x) => x.id),
            );
        }

        this.setState({ timesheet: { ...timesheet, timesheetDetails } });
    };

    onClose = () => {
        this.resetForm();
        this.props.toggleShow(false);
        this.context.setFormOpened(false);
        this.props.onClose();
    };

    paintBatchCodes = () => (this.state.timesheet.paintBatchCodes ?? '')
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);

    setPaintBatchCodes = (codes, currentPaintBatchCode) => this.setState((state) => ({
        ...state,
        currentPaintBatchCode,
        timesheet: { ...state.timesheet, paintBatchCodes: codes.join(',') },
    }));

    onAddPaintBatchCodes = (codes, pendingInput = '') => {
        const existing = this.paintBatchCodes();
        const kept = [];
        const rejected = [];
        let anyTooLong = false;
        let anyOverLimit = false;

        codes.forEach((entry) => {
            const code = entry.trim();

            if (!code || existing.includes(code) || kept.includes(code)) {
                return;
            }

            if (code.length > PAINT_BATCH_CODE_MAX_LENGTH) {
                anyTooLong = true;
                rejected.push(code);
                return;
            }

            if (existing.length + kept.length >= PAINT_BATCH_CODES_MAX_COUNT
                || existing.concat(kept, code).join(',').length > PAINT_BATCH_CODES_MAX_LENGTH) {
                anyOverLimit = true;
                rejected.push(code);
                return;
            }

            kept.push(code);
        });

        if (anyTooLong) {
            toasty.error(t('timesheet:paintBatchCodeTooLong', { max: PAINT_BATCH_CODE_MAX_LENGTH }));
        } else if (anyOverLimit) {
            toasty.error(t('timesheet:paintBatchCodesLimitReached', { max: PAINT_BATCH_CODES_MAX_COUNT }));
        }

        // A rejected code stays in the field instead of vanishing behind the toast.
        const nextInput = pendingInput || (codes.length === 1 ? rejected[0] ?? '' : '');

        this.setPaintBatchCodes(existing.concat(kept), nextInput);
    };

    onPaintBatchCodeChanged = (ev) => {
        const { value } = ev.target;

        if (!value.includes(',')) {
            this.setState({ currentPaintBatchCode: value });
            return;
        }

        // A comma finishes the code being typed and starts the next one.
        const parts = value.split(',');
        this.onAddPaintBatchCodes(parts.slice(0, -1), parts[parts.length - 1]);
    };

    onPaintBatchPhotosUploaded = async (uploads) => {
        if (!uploads?.length) {
            return;
        }

        this.setState({ attachmentsLoading: true });

        try {
            const attachments = await httpClient.put(
                ApiRoutes.timesheet.updateAttachments(this.state.timesheet.id, TimesheetAttachmentScope.PaintBatchPhotos),
                uploads.map((upload) => upload.id),
            );
            this.setState({ attachments });
        } catch {
            toasty.error(t('timesheet:attachmentsUpdateFailed'));
        } finally {
            this.setState({ attachmentsLoading: false });
        }
    };

    onPaintBatchPhotoDeleted = async (attachment) => {
        this.setState({ attachmentsLoading: true });

        try {
            const attachments = await httpClient.delete(
                ApiRoutes.timesheet.deleteAttachment(this.state.timesheet.id, attachment.id, TimesheetAttachmentScope.PaintBatchPhotos),
            );
            this.setState({ attachments });
        } catch {
            toasty.error(t('timesheet:attachmentDeleteFailed'));
        } finally {
            this.setState({ attachmentsLoading: false });
        }
    };

    onRemoveChargeTypeFromTimesheet = (chargeType) => {
        const { optionalChargeTypes, visibleChargeTypeIds, chargeTypes } = {
            ...this.state,
        };
        const updatedChargeTypeIndex = chargeTypes.indexOf(
            chargeTypes.find((c) => c.id === chargeType.id),
        );

        if (!chargeType.requiresWorkflowNotes) {
            optionalChargeTypes.push(chargeType);
        }

        const updatedChargeTypes = visibleChargeTypeIds.filter(
            (ct) => ct !== chargeType.id,
        );
        chargeTypes[updatedChargeTypeIndex].include = false;
        this.setState({
            optionalChargeTypes,
            visibleChargeTypeIds: updatedChargeTypes,
            chargeTypes,
        });
    };

    onSaveAddress = (address) => {
        if (this.state.timesheet.contractAutoCalculateDriveTimeBilling && (!address.latitude || !address.longitude)) {
            toasty.error(t('timesheet:addressMustBeGeocoded'));
            return;
        }
        this.setState((state) => ({
            ...state,
            timesheet: {
                ...state.timesheet,
                jobLocation: formatAddress(address),
                jobLocationAddress: { ...address },
                jobLocationId: null,
            },
            jobLocationOverride: address,
        }));
    };

    onClearJobLocation = () => {
        this.setState((state) => ({
            ...state,
            timesheet: {
                ...state.timesheet,
                jobLocation: state.timesheet.originalJobLocation ?? null,
                jobLocationAddress: state.timesheet.originalJobLocationAddress ?? null,
                jobLocationId: state.timesheet.originalJobLocationId ?? null,
            },
            jobLocationOverride: null,
        }));
    };

    onAddMeetingLocationAddress = () => {
        const { timesheet } = this.state;
        const tenant = this.context?.tenant ?? null;
        const existing = timesheet.meetingLocationAddress ?? timesheet.originalMeetingLocationAddress;
        const countryId = existing?.countryId ?? tenant?.countryId;
        const countryCode = existing?.countryCode ?? tenant?.countryCode;
        const addressToOpen = existing ? { ...existing } : new Address(countryId, countryCode);
        addressToOpen.id = null;
        this.meetingAddressFormRef.current.open(
            addressToOpen,
            'Meeting Location Override',
            false,
            false,
        );
    };

    onSaveMeetingAddress = (address) => {
        if (this.state.timesheet.contractAutoCalculateDriveTimeBilling && (!address.latitude || !address.longitude)) {
            toasty.error(t('timesheet:addressMustBeGeocoded'));
            return;
        }
        this.setState((state) => ({
            ...state,
            timesheet: {
                ...state.timesheet,
                meetingLocation: formatAddress(address),
                meetingLocationAddress: { ...address },
                meetingLocationId: null,
            },
            meetingLocationOverride: address,
        }));
    };

    onClearMeetingLocation = () => {
        this.setState((state) => ({
            ...state,
            timesheet: {
                ...state.timesheet,
                meetingLocation: state.timesheet.originalMeetingLocation ?? null,
                meetingLocationAddress: state.timesheet.originalMeetingLocationAddress ?? null,
                meetingLocationId: state.timesheet.originalMeetingLocationId ?? null,
            },
            meetingLocationOverride: null,
        }));
    };

    onSelectChange = onReactSelectChanged;

    onSubmitTimesheet = async () => {
        const { saving } = { ...this.state };
        if (saving) {
            return false;
        }

        this.setState({ saving: true });
        await this.saveTimesheet();
    };

    groupTimesheetDetails = () => {
        const { timesheet } = this.state;
        const detailsArr = timesheet?.timesheetDetails || [];
        const activeDetails = detailsArr.filter((d) => !d.removed);
        const timesheetDetails = groupBy(
            activeDetails,
            (asn) => asn.chargeTypeName,
        );
        return mapValues(timesheetDetails, (chargeGroup) => {
            let i = 0;
            let totalAFADs = 0;

            const len = (chargeGroup ?? []).length;

            const results = [];

            for (; i < len; i += 1) {
                const detail = chargeGroup[i];
                let description = detail.equipmentName ?? detail.employeeFullName;
                const { quantityLimit } = detail;

                const resources = [detail];

                if (detail.equipment?.equipmentType?.equipmentTypeAFADType?.isAFAD) {
                    totalAFADs += 1;

                    if (detail.chargeType.baseConfig.numAFADsInSet > 1) {
                        // Group everything under the first item in the set and discard all other items in the set
                        const isFirstInSet = totalAFADs % detail.chargeType.baseConfig.numAFADsInSet == 1;

                        if (!isFirstInSet) {
                            continue;
                        }

                        let j = 1;

                        for (; j < detail.chargeType.baseConfig.numAFADsInSet; j += 1) {
                            const next = chargeGroup[i + j];

                            if (!next?.equipment?.equipmentType?.equipmentTypeAFADType?.isAFAD || next.isBreak !== detail.isBreak) {
                                totalAFADs = 0; // Reset counter on invalid entry
                                break;
                            }

                            description = `${description}, ${next.equipmentName}`;
                            resources.push(next);
                        }
                    }
                }

                results.push({
                    ...detail, resources, description, quantityLimit,
                });
            }

            return results;
        });
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    open = async (timesheetId) => {
        this.setState({ loading: true });
        await this.populateState();

        const { allowDefaultChargeTypeValues } = this.state;

        let timesheet = null;

        this.resetForm();

        let attachments = [];

        if (timesheetId) {
            timesheet = await httpClient.js(
                ApiRoutes.timesheet.byId(timesheetId),
            );
            if (this.context?.tenant?.tenantSettings?.enablePaintBatchCodes) {
                try {
                    attachments = await httpClient.get(
                        ApiRoutes.timesheet.getAttachments(timesheetId, TimesheetAttachmentScope.PaintBatchPhotos),
                    );
                } catch {
                    // The photo list is not worth failing the whole slideout over.
                    attachments = [];
                }
            }
        } else {
            timesheet = new Timesheet();
        }

        const timesheetDetails = groupBy(
            timesheet.timesheetDetails,
            (asn) => asn.chargeTypeName,
        );



        const chargeTypeNames = Object.keys(timesheetDetails);

        if (allowDefaultChargeTypeValues) {
            timesheet.timesheetDetails.forEach((td) => {
                if (td.chargeTypeDefaultValue && !td.value) {
                    td.value = td.chargeTypeDefaultValue;
                }
            });
        }

        const [chargeTypes, requiredChargeTypeIds, optionalChargeTypes] = [
            // Stands up same as all? functionality
            chargeTypeNames
                .map((tsd) => {
                    const [ts] = timesheetDetails[tsd];
                    return {
                        id: ts.chargeTypeId,
                        name: ts.chargeTypeName,
                        unitsId: ts.chargeTypeUnitsId,
                        isRentalPickup: ts.isRentalPickup,
                        rentalDropoffQuantity: ts.jobAssignment?.rentalDropoffQuantity,
                        rentalPickupQuantity: ts.jobAssignment?.rentalPickupQuantity,
                        isOptional: ts.contractChargeTypeEligibilityId === ChargeTypeEligbility.Optional,
                        sameAll: false,
                        include: ts.include,
                        isSurcharge: ts.isSurcharge,
                        resourceTypeId: ts.chargeType.resourceTypeId,
                        payrollChargeTypeId: ts.chargeType.payrollChargeTypeId,
                        requiresWorkflowNotes: !!timesheet.jobType?.timesheetRequiresWorkflowNotes
                            && ts.chargeType.resourceTypeId === ChargeTypeResourceTypes.Setup,
                    };
                })
                .filter((x) => !!x),
            // Display all required/specified charge types immediately
            chargeTypeNames
                .map((tsd) => {
                    if (timesheetDetails[tsd][0].include) return timesheetDetails[tsd][0].chargeTypeId;
                    return null;
                })
                .filter((x) => !!x),
            // Save optional charge types until the user adds them.
            chargeTypeNames
                .map((tsd) => {
                    const [ts] = timesheetDetails[tsd];

                    if (
                        (
                            ts.contractChargeTypeEligibilityId === ChargeTypeEligbility.Optional
                            && !ts.include
                        ) || (
                            ts.chargeType.resourceTypeId === ChargeTypeResourceTypes.Setup
                            && timesheet.jobType?.timesheetRequiresWorkflowNotes
                        )
                    ) {
                        return {
                            id: timesheetDetails[tsd][0].chargeTypeId,
                            name: timesheetDetails[tsd][0].chargeTypeName,
                            payrollChargeTypeId: timesheetDetails[tsd][0].chargeType?.payrollChargeTypeId,
                            requiresWorkflowNotes: !!timesheet.jobType?.timesheetRequiresWorkflowNotes
                                && ts.chargeType.resourceTypeId === ChargeTypeResourceTypes.Setup,
                        };
                    }
                    return null;
                })
                .filter((x) => !!x),
        ];

        this.setState({
            timesheet,
            chargeTypes,
            visibleChargeTypeIds: requiredChargeTypeIds,
            optionalChargeTypes,
            contractId: timesheet.contractId,
            attachments,
            currentPaintBatchCode: '',
        });

        this.props.toggleShow(true);
        this.context.setFormOpened(true);

        this.setState({ loading: false });
    };

    populateState() {
        const { allowDefaultChargeTypeValues } = this.context?.tenant?.tenantSettings;

        this.setState({ allowDefaultChargeTypeValues });
    }


    resetForm = () => {
        this.setState({
            formValidated: false,
        });
    };

    saveTimesheet = async () => {
        const { timesheet, chargeTypes, currentPaintBatchCode } = { ...this.state };

        // A code typed but never added is still a code the crew recorded — save it too.
        const pendingPaintBatchCode = (currentPaintBatchCode ?? '').trim();
        const savedPaintBatchCodes = this.paintBatchCodes();

        if (
            pendingPaintBatchCode
            && !savedPaintBatchCodes.includes(pendingPaintBatchCode)
            && pendingPaintBatchCode.length <= PAINT_BATCH_CODE_MAX_LENGTH
            && savedPaintBatchCodes.length < PAINT_BATCH_CODES_MAX_COUNT
            && savedPaintBatchCodes.concat(pendingPaintBatchCode).join(',').length <= PAINT_BATCH_CODES_MAX_LENGTH
        ) {
            timesheet.paintBatchCodes = savedPaintBatchCodes.concat(pendingPaintBatchCode).join(',');
        }

        timesheet.timesheetDetails = timesheet.timesheetDetails.map((d) => {
            d.include = chargeTypes.find(
                (c) => c.id === d.chargeTypeId,
            )?.include || false;

            if (typeof d.id === 'string') {
                d.id = 0;
            }

            // Leaving this in the object causes issues with automapper
            delete d.jobAssignment;

            return d;
        });

        const setupCharges = timesheet.timesheetDetails.filter(
            (x) => x.chargeType.resourceTypeId === ChargeTypeResourceTypes.Setup && x.include == true && (x.isSurcharge == null || x.isSurcharge == false),
        );

        if (timesheet.contractSetupChargesRequired && setupCharges.length == 0) {
            toasty.error(t('timesheet:minimumOneSetupCharge'));
            this.setState({ saving: false });
            return;
        }

        await httpClient
            .post(ApiRoutes.timesheet.update(), timesheet)
            .then((updatedTimesheet) => {
                this.setState({ timesheet: { ...updatedTimesheet } });
                toasty.success(
                    'Timesheet Saved',
                    'Timesheet updated successfully.',
                );
                this.onClose();
            })
            .catch(() => toasty.error(t('timesheet:errorSaving')))
            .finally(() => this.setState({ saving: false }));
    };

    toggleAddChargeDropdownOpen = () => this.setState(
        (state) => (
            (state.addChargeDropdownOpen = !state.addChargeDropdownOpen),
            state
        ),
    );

    toggleDetailNotApplicable = (notApplicable, resources = []) => {
        const { timesheet } = this.state;

        const timesheetDetails = timesheet.timesheetDetails.map((detail) => {
            const resource = resources.findIndex((x) => x.id === detail.id);

            if (resource === -1) {
                return detail;
            }

            return {
                ...detail,
                notApplicable,
                start: '',
                end: '',
                value: 0,
            };
        });

        this.setState({ timesheet: { ...timesheet, timesheetDetails } });
    };

    render() {
        const {
            addChargeDropdownOpen,
            chargeTypes,
            formValidated,
            optionalChargeTypes,
            timesheet,
            validationMessage,
            saving,
            loading,
            errors,
            usCounties,
            currentPaintBatchCode,
            attachments,
            attachmentsLoading,
        } = this.state;

        if (!timesheet) {
            return null;
        }

        const timesheetDetails = this.groupTimesheetDetails();
        const paintBatchCodes = this.paintBatchCodes();
        const enablePaintBatchCodes = !!this.context?.tenant?.tenantSettings?.enablePaintBatchCodes;

        const wonPlaceholder = timesheet.workOrderTypeId === WorkOrderType.Required
            ? t('timesheet:requiredPlaceholder')
            : t('timesheet:optional');
        const wrPlaceholder = t('timesheet:optional');
        const poPlaceholder = t('timesheet:optional');

        const disabled = !!timesheet.isComplete;

        return (
            <SlideForm
                loading={loading}
                show={this.props.show}
                id="timesheetItem"
                formIcon={faUserClock}
                formTitle={
                    !!parseInt((timesheet ?? {}).id ?? 0) > 0
                        ? t('timesheet:editTimesheet')
                        : t('timesheet:addTimesheet')
                }
                ref={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={formValidated}
                onSubmit={this.onSubmitTimesheet}
                onClose={this.onClose}
                onSave={this.saveTimesheet}
                onDelete={this.onDelete}
                errors={errors}
                onClearErrors={this.onClearErrors}
                error={validationMessage}
                readOnly={disabled}
            >
                <FormBlocker
                    show={!!saving}
                    showProgress
                />
                <div className="timesheetDetails">
                    <h5 className="d-flex flex-row align-items-center text-muted justify-content-center">
                        {t('timesheet:timesheetTitleParts', {
                            customer: timesheet.customerName,
                            subcontractorPart: timesheet.subcontractorName ? t('timesheet:subcontractorPart', { name: timesheet.subcontractorName }) : '',
                            jobNumber: timesheet.jobNumber,
                        })}
                        {timesheet.timesheetSequence > 0 && (
                            <Badge
                                className="ml-1"
                                size="lg"
                            >
                                {timesheet.timesheetSequence}
                            </Badge>
                        )}
                    </h5>

                    <div
                        id="validationMessages"
                        className="pt-2 pb-2"
                    >
                        <small className="text-danger">
                            <FontAwesomeIcon
                                icon={faExclamationCircle}
                                size="lg"
                                className="mr-1"
                            />
                            {t('timesheet:submissionInvalid')}
                        </small>
                    </div>

                    {!!timesheet.isComplete && (
                        <div className="site-alert site-alert-success text-center">
                            {t('timesheet:timesheetCompleted')}
                        </div>
                    )}

                    {!!timesheet.linkedTimesheetId && (
                        <FlexStartRow>
                            <DatePicker
                                id="shiftDate"
                                name="timesheet.shiftDate"
                                label="Shift Start Date"
                                disabled={disabled}
                                portalId="shift-date-picker-portal"
                                selected={
                                    timesheet.shiftDate
                                        ? moment(timesheet.shiftDate).toDate()
                                        : moment(timesheet.jobDate).toDate()
                                }
                                minDate={moment(timesheet.jobDate).toDate()}
                                onChange={(ev) => {
                                    const { value } = ev.target;
                                    this.setState(
                                        (state) => (
                                            (state.timesheet.shiftDate = value
                                                ? moment(value).format('YYYY-MM-DD')
                                                : null),
                                            state
                                        ),
                                    );
                                }}
                            />
                        </FlexStartRow>
                    )}

                    <FlexBetweenRow>
                        {timesheet.workOrderTypeId
                            !== WorkOrderType.NotApplicable /* NA */ && (
                                <FlexStartRow>
                                    <Input
                                        label={t('timesheet:workOrderHashLabel')}
                                        placeholder={wonPlaceholder}
                                        disabled={disabled}
                                        id="workOrderNumberTimesheet"
                                        required={
                                            timesheet.workOrderTypeId
                                            == WorkOrderType.Required
                                        }
                                        name="timesheet.workOrderNumber"
                                        formGroupClassName="w-100"
                                        className={cls(
                                            'form-control-sm',
                                        )}
                                        value={
                                            timesheet.workOrderNumber
                                            ?? ''
                                        }
                                        onChange={(ev) => {
                                            const { value } = ev.target;
                                            this.setState(
                                                (state) => (
                                                    (state.timesheet.workOrderNumber = value),
                                                    state
                                                ),
                                            );
                                        }}
                                        maxLength={44}
                                    />
                                </FlexStartRow>
                            )}
                        {!!optionalChargeTypes.length && !timesheet.isComplete && (
                            <FormGroup>
                                <FormLabel
                                    htmlFor="addChargeDropdown"
                                    label={t('timesheet:addOptionalCharge')}
                                    className="control-label"
                                    style={{ opacity: 0 }}
                                />
                                <Dropdown
                                    isOpen={addChargeDropdownOpen}
                                    toggle={
                                        this.toggleAddChargeDropdownOpen
                                    }
                                >
                                    <DropdownToggle
                                        size="sm"
                                        id="addChargeDropdown"
                                        color="outline-primary"
                                    >
                                        <i className="fa fa-plus-circle fa-lg mr-1" />
                                        {t('timesheet:addOptionalCharge')}
                                    </DropdownToggle>
                                    <DropdownMenu className="shadow">
                                        {optionalChargeTypes.map((ct) => (
                                            <DropdownItem
                                                key={ct.id}
                                                className="cursor-pointer"
                                                onClick={() => this.onAddChargeTypeToTimesheet(
                                                    ct,
                                                )}
                                            >
                                                {ct.name}
                                            </DropdownItem>
                                        ))}
                                    </DropdownMenu>
                                </Dropdown>
                            </FormGroup>
                        )}
                    </FlexBetweenRow>
                    {timesheet.requiresPurchaseOrderNumber && (
                        <FlexStartRow>
                            <Input
                                label={t('timesheet:purchaseOrderHashLabel')}
                                placeholder={poPlaceholder}
                                disabled={disabled}
                                id="purchaseOrderNumberTimesheet"
                                name="timesheet.purchaseOrderNumber"
                                className={cls(
                                    'form-control-sm',
                                )}
                                value={
                                    timesheet.purchaseOrderNumber
                                    ?? ''
                                }
                                onChange={(ev) => {
                                    const { value } = ev.target;
                                    this.setState(
                                        (state) => (
                                            (state.timesheet.purchaseOrderNumber = value),
                                            state
                                        ),
                                    );
                                }}
                                maxLength={44}
                            />
                        </FlexStartRow>
                    )}
                    {timesheet.requiresWorkRequest && (
                        <FlexStartRow>
                            <Input
                                label={t('timesheet:workRequestHashLabel')}
                                placeholder={wrPlaceholder}
                                disabled={disabled}
                                id="wrTimesheet"
                                name="timesheet.workRequest"
                                formGroupClassName="w-100"
                                className={cls(
                                    'form-control-sm',
                                )}
                                value={
                                    timesheet.workRequest
                                    ?? ''
                                }
                                onChange={(ev) => {
                                    const { value } = ev.target;
                                    this.setState(
                                        (state) => (
                                            (state.timesheet.workRequest = value),
                                            state
                                        ),
                                    );
                                }}
                                maxLength={44}
                            />
                        </FlexStartRow>
                    )}
                    <FlexStartRow>
                        <div className="form-group w-100">
                            <FormLabel
                                htmlFor="jobLocationOverride"
                                label={t('timesheet:jobLocationOverride')}
                            />
                            <div className="d-flex flex-row align-items-center">
                                <Input
                                    id="jobLocationOverride"
                                    name="timesheet.jobLocation"
                                    formGroupClassName="flex-fill"
                                    className="form-control-sm"
                                    value={timesheet.jobLocation ?? ''}
                                    readOnly
                                    placeholder={t('timesheet:optional')}
                                    endAddon={(
                                        <>
                                            {timesheet.contractAutoCalculateDriveTimeBilling && timesheet.jobLocation && !timesheet.jobLocationAddress && (
                                                <>
                                                    <FontAwesomeIcon
                                                        id="invalid-job-location-override"
                                                        icon={faExclamationTriangle}
                                                        className="text-warning mb-2"
                                                    />
                                                    <UncontrolledTooltip target="invalid-job-location-override">
                                                        {t('timesheet:addressNotGeocoded')}
                                                    </UncontrolledTooltip>
                                                </>
                                            )}
                                        </>
                                    )}
                                />
                                {!disabled && (
                                    <Button
                                        color="link"
                                        size="sm"
                                        className="text-danger p-0 ml-1"
                                        onClick={this.onAddJobLocationAddress}
                                    >
                                        {t('common:edit')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </FlexStartRow>
                    <FlexStartRow>
                        <div className="form-group w-100">
                            <FormLabel
                                htmlFor="meetingLocationOverride"
                                label={t('timesheet:meetingLocationOverride')}
                            />
                            <div className="d-flex flex-row align-items-center">
                                <Input
                                    id="meetingLocationOverride"
                                    name="timesheet.meetingLocation"
                                    formGroupClassName="flex-fill"
                                    className="form-control-sm"
                                    value={timesheet.meetingLocation ?? ''}
                                    readOnly
                                    placeholder={t('timesheet:optional')}
                                    endAddon={(
                                        <>
                                            {timesheet.contractAutoCalculateDriveTimeBilling && timesheet.meetingLocation && !timesheet.meetingLocationAddress && (
                                                <>
                                                    <FontAwesomeIcon
                                                        id="invalid-meeting-location-override"
                                                        icon={faExclamationTriangle}
                                                        className="text-warning mb-2"
                                                    />
                                                    <UncontrolledTooltip target="invalid-meeting-location-override">
                                                        {t('timesheet:addressNotGeocoded')}
                                                    </UncontrolledTooltip>
                                                </>
                                            )}
                                        </>
                                    )}
                                />
                                {!disabled && (
                                    <Button
                                        color="link"
                                        size="sm"
                                        className="text-danger p-0 ml-1"
                                        onClick={this.onAddMeetingLocationAddress}
                                    >
                                        {t('common:edit')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </FlexStartRow>
                    {!!chargeTypes && chargeTypes.filter((ct) => ct.include && !ct.isSurcharge)
                        .map((chargeType) => {
                            const group = timesheetDetails[chargeType.name] || [];

                            if (group.length === 0) return null;

                            return (
                                <div
                                    key={chargeType.name}
                                    className="crewTimesheetDetails table-responsive"
                                >
                                    <FlexBetweenRow>
                                        <span className="pl-1 timesheetHeader text-muted">
                                            {chargeType.name}

                                            {chargeType.unitsId === ChargeTypeUnits.Flat && chargeType.resourceTypeId === ChargeTypeResourceTypes.Rental && (
                                                <>
                                                    {' '}
                                                    {t('timesheet:requestedPrefix')}
                                                    {' '}
                                                    {chargeType.isRentalPickup ? chargeType.rentalPickupQuantity : chargeType.rentalDropoffQuantity}
                                                </>
                                            )}

                                            {chargeType.id === 1 && ( // min charge type is 1
                                                <FontAwesomeIcon
                                                    icon={
                                                        faExclamationCircle
                                                    }
                                                    title={t('timesheet:minimumChargeFromCancel')}
                                                    className="text-danger ml-2 cursor-pointer"
                                                />
                                            )}

                                            {
                                                // Only allow optional charge types to be removed once added.
                                                !!chargeType.isOptional && (
                                                    <FontAwesomeIcon
                                                        icon={
                                                            faMinusCircle
                                                        }
                                                        title={t('timesheet:removeChargeType')}
                                                        className="text-danger ml-2 cursor-pointer"
                                                        onClick={() => this.onRemoveChargeTypeFromTimesheet(
                                                            chargeType,
                                                        )}
                                                    />
                                                )
                                            }
                                        </span>

                                        {!timesheet.isComplete
                                            && chargeType.id !== 1
                                            && chargeType.resourceTypeId !== ChargeTypeResourceTypes.Setup
                                            && group.length > 1
                                            && (
                                                <FormCheckbox
                                                    small
                                                    className="m-1"
                                                    id={`${chargeType.name}`}
                                                    name={`${chargeType.name}`}
                                                    checked={chargeType.sameAll}
                                                    onChange={this.onAllCheckChanged}
                                                    labelText={t('timesheet:sameForAll')}
                                                />
                                            )}

                                        {chargeType.resourceTypeId === ChargeTypeResourceTypes.Setup && <div style={{ height: '25px' }}>&nbsp;</div>}
                                    </FlexBetweenRow>

                                    <table className="table table-sm crewTimesheetTable borderless">
                                        {chargeType.resourceTypeId !== ChargeTypeResourceTypes.Setup ? (
                                            <thead>
                                                <tr>
                                                    <th />
                                                    <th className="not-applicable" />
                                                    <th className="not-applicable">
                                                        {t('timesheet:notAvailable')}
                                                    </th>

                                                    {chargeType.unitsId === ChargeTypeUnits.Flat || chargeType.payrollChargeTypeId > 0 ? (
                                                        <>
                                                            <th className="start-time" />

                                                            <th className="units-value">
                                                                {group[0]?.rentalUnitTypeId === RentalUnitTypes.Hourly ? t('timesheet:qty') : null}
                                                            </th>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <th className="start-time">
                                                                {t('timesheet:startUpper')}
                                                            </th>
                                                            <th className="end-time">
                                                                {t('timesheet:stopUpper')}
                                                            </th>
                                                        </>
                                                    )}
                                                </tr>
                                            </thead>
                                        ) : (
                                            <>
                                                {Boolean(chargeType.requiresWorkflowNotes) && (
                                                    <thead>
                                                        <th className="not-applicable">
                                                            {t('timesheet:notAvailable')}
                                                        </th>

                                                        <th className="units-value">
                                                            {t('timesheet:notes')}
                                                        </th>

                                                        <th className="units-value">
                                                            {t('timesheet:qty')}
                                                        </th>
                                                    </thead>
                                                )}
                                            </>
                                        )}

                                        <tbody>
                                            {group.map((detail) => {
                                                let timeSpan = 0.0;
                                                let chargeExceedsThreshold = false;
                                                let breakExceedsThreshold = false;

                                                if (
                                                    detail.start != null
                                                    && detail.start.length > 0
                                                    && (
                                                        detail.hoursExceededThreshold > 0
                                                        || detail.breakHoursExceededThreshold > 0
                                                    )
                                                ) {
                                                    const start = moment(detail.start, 'hh:mm');
                                                    const end = moment(detail.end, 'hh:mm');
                                                    const dur = moment.duration(
                                                        end.diff(
                                                            start,
                                                        ),
                                                    );

                                                    timeSpan = dur._data
                                                        .hours
                                                        + (dur._data
                                                            .minutes
                                                            > 0
                                                            ? dur
                                                                ._data
                                                                .minutes
                                                            / 60
                                                            : 0);

                                                    chargeExceedsThreshold = !detail.isBreak
                                                        && detail.hoursExceededThreshold
                                                        > 0
                                                        && timeSpan
                                                        >= detail.hoursExceededThreshold;

                                                    breakExceedsThreshold = detail.isBreak
                                                        && detail.breakHoursExceededThreshold
                                                        > 0
                                                        && timeSpan
                                                        >= detail.breakHoursExceededThreshold;
                                                }

                                                return (
                                                    <tr
                                                        key={
                                                            detail.id
                                                        }
                                                        className={
                                                            detail.start
                                                                && detail.end
                                                                && detail.start > detail.end
                                                                ? 'ag-cell-danger'
                                                                : ''
                                                        }
                                                    >
                                                        {!chargeType.requiresWorkflowNotes && (
                                                            <>
                                                                <td>
                                                                    <small>
                                                                        {detail.description}

                                                                        {detail.quantityLimit && detail.quantityLimit >= 0 && detail.chargeTypeUnitsId === ChargeTypeUnits.Flat && (
                                                                            <span color="error">
                                                                                {t('timesheet:maximumWithValue', { max: detail.quantityLimit })}
                                                                            </span>
                                                                        )}

                                                                        {detail.estimatedDriveTime > 0 && (
                                                                            <FlexStartRow>
                                                                                <span className="text-muted text-small">
                                                                                    {t('timesheet:estimatedTravelTime')}
                                                                                    {' '}
                                                                                    {formatMinutes(detail.estimatedDriveTime)}
                                                                                </span>
                                                                            </FlexStartRow>
                                                                        )}
                                                                    </small>
                                                                </td>

                                                                <td className="not-applicable">
                                                                    {chargeExceedsThreshold && (
                                                                        <i
                                                                            title={t('timesheet:exceedsThreshold')}
                                                                            className="fa fa-exclamation-triangle text-warning mr-1"
                                                                        />
                                                                    )}
                                                                    {breakExceedsThreshold && (
                                                                        <i
                                                                            title={t('timesheet:exceedsThreshold')}
                                                                            className="fa fa-exclamation-triangle text-warning mr-1"
                                                                        />
                                                                    )}
                                                                </td>
                                                            </>
                                                        )}

                                                        <td className="not-applicable">
                                                            {(chargeType.resourceTypeId !== ChargeTypeResourceTypes.Setup || detail.quantityLimit >= 0) && (
                                                                <FontAwesomeIcon
                                                                    size="lg"
                                                                    className={cls(
                                                                        'cursor-pointer icon-check',
                                                                        {
                                                                            checked: detail.notApplicable,
                                                                        },
                                                                    )}
                                                                    disabled={
                                                                        disabled
                                                                        || detail.timesheetDetailStatusId === 4
                                                                        || detail.disallowNA
                                                                    }
                                                                    icon={faBan}
                                                                    title={t('timesheet:toggleStartStopApplies')}
                                                                    onClick={
                                                                        timesheet.isComplete
                                                                            || detail.timesheetDetailStatusId === 4
                                                                            || detail.disallowNA
                                                                            ? () => false
                                                                            : () => this.toggleDetailNotApplicable(!detail.notApplicable, detail.resources)
                                                                    }
                                                                />
                                                            )}
                                                        </td>

                                                        {Boolean(chargeType.requiresWorkflowNotes) && (
                                                            <td className="units-value">
                                                                <Input
                                                                    className="form-control-sm"
                                                                    name="workflowNotes"
                                                                    disabled={
                                                                        !!detail.notApplicable
                                                                        || disabled
                                                                        || detail.timesheetDetailStatusId === 4
                                                                    }
                                                                    value={detail.workflowNotes ?? ''}
                                                                    onChange={(ev) => this.onChargeChange(
                                                                        detail.resources,
                                                                        ev,
                                                                    )}
                                                                    placeholder={t('timesheet:enterFieldNote')}
                                                                    required
                                                                />
                                                            </td>
                                                        )}

                                                        {detail.chargeTypeUnitsId === ChargeTypeUnits.Flat || detail.chargeType.payrollChargeTypeId > 0 ? (
                                                            <>
                                                                {detail.rentalUnitTypeId === RentalUnitTypes.Hourly && (
                                                                    <td className="start-time">
                                                                        <TimePicker
                                                                            name="start"
                                                                            value={detail.start} // Value must be in HH:mm format
                                                                            disabled={
                                                                                !!detail.notApplicable || disabled || detail.timesheetDetailStatusId === 4
                                                                            }
                                                                            increment={0.25}
                                                                            onChange={(ev) => this.onChargeChange(detail.resources, ev)}
                                                                            required
                                                                        />
                                                                    </td>
                                                                )}

                                                                <td
                                                                    className="units-value"
                                                                    colSpan={detail.rentalUnitTypeId === RentalUnitTypes.Hourly ? 1 : 2}
                                                                >
                                                                    <NumericInput
                                                                        className="form-control-sm"
                                                                        name="value"
                                                                        disabled={
                                                                            !!detail.notApplicable
                                                                            || disabled
                                                                            || detail.timesheetDetailStatusId === 4
                                                                        }
                                                                        readOnly={detail.chargeType.payrollChargeTypeId > 0}
                                                                        value={detail.value ?? ''}
                                                                        onChange={(ev) => this.onChargeChange(
                                                                            detail.resources,
                                                                            ev,
                                                                        )}
                                                                        min={0}
                                                                        max={detail.resources.length > 1 ? 1 : undefined}
                                                                        placeholder={t('timesheet:enterValueLabel')}
                                                                        step={detail.chargeTypeIncrement ?? 1}
                                                                    />
                                                                </td>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <td className="start-time">
                                                                    <TimePicker
                                                                        name="start"
                                                                        value={detail.start} // Value must be in HH:mm format
                                                                        disabled={
                                                                            !!detail.notApplicable || disabled || detail.timesheetDetailStatusId === 4
                                                                        }
                                                                        increment={detail.chargeTypeIncrement}
                                                                        onChange={(ev) => this.onChargeChange(detail.resources, ev)}
                                                                        required
                                                                    />
                                                                </td>
                                                                <td className="end-time">
                                                                    <TimePicker
                                                                        name="end"
                                                                        value={detail.end} // Value must be in HH:mm format
                                                                        disabled={
                                                                            !!detail.notApplicable || disabled || detail.timesheetDetailStatusId === 4
                                                                        }
                                                                        increment={detail.chargeTypeIncrement}
                                                                        onChange={(ev) => this.onChargeChange(detail.resources, ev)}
                                                                        required
                                                                    />
                                                                </td>
                                                            </>
                                                        )}
                                                    </tr>
                                                );
                                            },
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })
                    }

                    {enablePaintBatchCodes && (
                    <FormGroup className="paintBatchSection">
                        <FormLabel
                            className="w-100"
                            htmlFor="paintBatchCode"
                            label={t('timesheet:paintBatchCode')}
                        />

                        {/*
                            EmailRecipients is a generic "type a value, Enter/Add it into a
                            removable chip list" widget, nothing about it is email-specific
                            beyond its prop names. Reused here for paint batch codes rather
                            than duplicating that chip-list plumbing.
                        */}
                        <EmailRecipients
                            emailAddresses={paintBatchCodes}
                            currentEmailAddress={currentPaintBatchCode}
                            onEmailChanged={this.onPaintBatchCodeChanged}
                            onEmailAdded={() => this.onAddPaintBatchCodes([currentPaintBatchCode])}
                            onEmailRemoved={(code) => this.setPaintBatchCodes(
                                paintBatchCodes.filter((c) => c !== code),
                                currentPaintBatchCode,
                            )}
                            readOnly={disabled}
                            InputProps={{
                                id: 'paintBatchCode',
                                name: 'paintBatchCode',
                                type: 'text',
                                className: 'form-control-sm inputContainedEnd',
                                placeholder: t('timesheet:paintBatchCodePlaceholder'),
                                endAddonContained: false,
                                endAddonClassName: 'ml-2',
                                endAddon: (
                                    <Button
                                        className="btn-sm"
                                        type="button"
                                        color="primary"
                                        disabled={!currentPaintBatchCode.trim()}
                                        onClick={() => this.onAddPaintBatchCodes([currentPaintBatchCode])}
                                    >
                                        {t('common:add')}
                                    </Button>
                                ),
                            }}
                        />

                        {timesheet.id > 0 && (
                            <FileUpload
                                id="paintBatchPhotos"
                                url={ApiRoutes.fileUpload.createAttachment()}
                                accept={['.jpg', '.jpeg', '.png', '.gif']}
                                files={attachments}
                                onQueueComplete={this.onPaintBatchPhotosUploaded}
                                onDownload={onFileDownload}
                                onDelete={this.onPaintBatchPhotoDeleted}
                                type="simple"
                                buttonText={t('timesheet:paintBatchPhotos')}
                                buttonProps={{ selected: true, className: 'mt-2 ml-0 mb-2' }}
                                buttonIcon={faUpload}
                                stacked
                                fileIcon={faFileImage}
                                disabled={disabled || attachmentsLoading}
                            />
                        )}
                    </FormGroup>
                    )}

                    <FlexStartRow>
                        <Textarea
                            formGroupClassName="w-100"
                            label={t('timesheet:notes')}
                            disabled={disabled}
                            placeholder={t('timesheet:notesOptional')}
                            maxLength="2000"
                            id="notes"
                            rows="3"
                            name="timesheet.notes"
                            className="form-control-sm"
                            value={timesheet.notes ?? ''}
                            onChange={(ev) => {
                                const { value } = ev.target;
                                this.setState(
                                    (state) => (
                                        (state.timesheet.notes = value),
                                        state
                                    ),
                                );
                            }}
                        />
                    </FlexStartRow>
                </div>
                <AddressFormNew
                    ref={this.addressFormRef}
                    onSaveCallback={(address) => this.onSaveAddress(address)}
                    countryId={this.context?.tenant?.countryId}
                    countryCode={this.context?.tenant?.countryCode}
                    contractId={this.state.contractId}
                    usCounties={usCounties}
                    isUnionWork={timesheet?.isUnionWork}
                    contractUsesPrevailingWage={timesheet?.contractIsPrevailingWage}
                />
                <AddressFormNew
                    ref={this.meetingAddressFormRef}
                    onSaveCallback={(address) => this.onSaveMeetingAddress(address)}
                    countryId={this.context?.tenant?.countryId}
                    countryCode={this.context?.tenant?.countryCode}
                    contractId={this.state.contractId}
                    usCounties={usCounties}
                    isUnionWork={timesheet?.isUnionWork}
                    contractUsesPrevailingWage={timesheet?.contractIsPrevailingWage}
                />
            </SlideForm>
        );
    }
}
